import React, { useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { canView, canEdit, canDelete } from "./modulePermissions";

// Shared role-based access control for the Settings modules (Branches, Branch
// Classes, Sections, Organization Profile, ...). One place so every module denies
// access the same way and the wording/behaviour can't drift apart.
//
// The house rule this encodes:
//
//   * NOTHING is hidden, disabled or redirected away because of a permission. Every
//     button stays visible and clickable for every role.
//   * The check runs INSIDE the event handler / API call. A role without the grant
//     clicks the button, the guard intercepts, the Access Denied modal opens, and the
//     action does not happen.
//   * is_view === 0 is the one exception to "show everything": the page body is
//     replaced by an Access Denied card so the module's DATA is never rendered.
//
// Admins (userlevel 1/2) and roles with no configured grant rows keep full access —
// see can() in ./modulePermissions.
//
// ── Usage in a class component ──────────────────────────────────────────────────
//
//   import { attachModuleGuard, AccessDeniedModal, AccessDeniedCard } from "../../../utils/moduleGuard";
//
//   const BRANCHES_MODULE = ["branches"];   // module_key ONLY — see the note below
//
//   constructor(props) {
//     super(props);
//     this.state = { ..., accessDeniedShow: false };
//     this.guard = attachModuleGuard(this, BRANCHES_MODULE);
//   }
//
//   handleDelete = (row) => {
//     if (!this.guard.requireDelete()) return;   // pops the modal, stops the delete
//     ...
//   };
//
//   render() {
//     if (!this.guard.canView()) return <AccessDeniedCard />;
//     return (
//       <div>
//         ...
//         <AccessDeniedModal show={this.state.accessDeniedShow} onHide={this.guard.hideAccessDenied} />
//       </div>
//     );
//   }
//
// ── Always pass module_key, never module_name ───────────────────────────────────
// A grant row looks like { module_key, module_name, is_view, is_edit, is_delete }.
// module_name is the SIDEBAR GROUP ("Settings", "Online Exams"), shared by every
// module under it. can() allows an action when ANY matched row grants it, so matching
// on the group name would let a role with edit on one Settings module inherit edit on
// all of them. module_key is unique per module — it is what the sidebar keys off.

// The state key the guard flips to open the modal. Components must include it in
// their initial state (accessDeniedShow: false) and render <AccessDeniedModal>.
const FLAG = "accessDeniedShow";

// Binds the permission checks for `moduleKeys` to a component instance.
// `moduleKeys` is an array of accepted module_key spellings.
//
// `subModule` (optional): when several rows share the same module_key and differ only
// by sub_module_name (e.g. Settings/branches has a "branches" row AND a "classes" row),
// pass the sub_module_name so the check reads ONLY that row. Without it, can() matches
// every row with the module_key and grants the action if ANY of them allows it — so a
// "classes" grant would leak into the "branches" page. See can() in modulePermissions.
export function attachModuleGuard(component, moduleKeys, subModule = null) {
  const showAccessDenied = () => component.setState({ [FLAG]: true });
  const hideAccessDenied = () => component.setState({ [FLAG]: false });

  // require*() returns false and opens the modal when the grant is missing, so call
  // sites read as a single early-return guard:  if (!this.guard.requireEdit()) return;
  const require = (allowed) => () => {
    if (allowed()) return true;
    showAccessDenied();
    return false;
  };

  return {
    // Plain predicates — no side effect. Use for the render-time is_view check.
    canView: () => canView(moduleKeys, subModule),
    canEdit: () => canEdit(moduleKeys, subModule),
    canDelete: () => canDelete(moduleKeys, subModule),

    // Guards — check, and open the Access Denied modal if the grant is missing.
    // requireEdit covers Create / Edit / Update / Save; requireDelete covers removal.
    requireEdit: require(() => canEdit(moduleKeys, subModule)),
    requireDelete: require(() => canDelete(moduleKeys, subModule)),

    showAccessDenied,
    hideAccessDenied,
  };
}

// Hook form of attachModuleGuard, for function components. Same contract, except the
// guard owns its own modal flag instead of writing to a component's state — so the
// caller renders <AccessDeniedModal show={guard.accessDeniedShow} onHide={guard.hideAccessDenied} />.
//
// The returned object is stable across renders except for `accessDeniedShow`, so it can
// be passed down to child components as a prop without forcing needless re-renders.
export function useModuleGuard(moduleKeys) {
  const [accessDeniedShow, setAccessDeniedShow] = useState(false);

  // moduleKeys is expected to be a module-level constant; keying the memo on its
  // identity is intentional. Pass a literal array inline and you'd rebuild every render.
  const api = useMemo(() => {
    const showAccessDenied = () => setAccessDeniedShow(true);
    const require = (allowed) => () => {
      if (allowed()) return true;
      showAccessDenied();
      return false;
    };
    return {
      canView: () => canView(moduleKeys),
      canEdit: () => canEdit(moduleKeys),
      canDelete: () => canDelete(moduleKeys),
      requireEdit: require(() => canEdit(moduleKeys)),
      requireDelete: require(() => canDelete(moduleKeys)),
      showAccessDenied,
      hideAccessDenied: () => setAccessDeniedShow(false),
    };
  }, [moduleKeys]);

  return { ...api, accessDeniedShow };
}

const ICON = {
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: "#fef2f2",
  color: "#dc2626",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
};

// Shown when a role clicks an action it lacks the grant for. One modal per module
// serves every intercepted action (create / edit / update / delete).
export const AccessDeniedModal = ({
  show,
  onHide,
  message = "You don't have permission to perform this action.",
}) => (
  <Modal show={show} onHide={onHide} centered size="sm" aria-labelledby="access-denied-title">
    <Modal.Body className="text-center" style={{ padding: "28px 24px" }}>
      <div style={ICON}>
        <i className="fal fa-lock-alt" style={{ fontSize: 26 }} />
      </div>
      <h5 id="access-denied-title" style={{ fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>
        Access Denied
      </h5>
      <p style={{ fontSize: 14, color: "#64748b", marginBottom: 20 }}>{message}</p>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Body>
  </Modal>
);

// Replaces the module's body when is_view === 0, so the data is never rendered.
// This is a CARD, not a full page: the Settings pages already supply the navbar,
// sidebar and footer, and the user keeps them so they can navigate elsewhere.
// (moduleAccessGuard exports a full-shell AccessDenied for the route-level case.)
export const AccessDeniedCard = ({
  message = "You don't have access to this module. Please contact your administrator.",
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "70vh",
      padding: 24,
    }}
  >
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        padding: "40px 44px",
        maxWidth: 460,
        textAlign: "center",
      }}
    >
      <div style={{ ...ICON, width: 72, height: 72, margin: "0 auto 18px" }}>
        <i className="fal fa-lock-alt" style={{ fontSize: 30 }} />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
        Access Denied
      </h3>
      <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 0 }}>{message}</p>
    </div>
  </div>
);
